import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react';
import { Pressable, View, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useModelStore, useAuthStore, useModalStore } from '@/store';
import { toolsApi, promptsApi } from '@/lib/api';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ToolFormField from '@/components/Tools/ToolFormField';
import ToolModelButton from '@/components/Tools/ToolModelButton';
import ToolRunButton from '@/components/Tools/ToolRunButton';
import {
  estimateToolRunCreditCost,
  getCompatibleToolModelId,
  getFallbackToolPickerModels,
  getPreferredToolModel,
  isToolModelPlusLocked,
} from '@/components/Tools/toolDetailHelpers';
import { openModelAccessPaywall } from '@/constants/utils/trial';
import { formatCreditResetDate } from '@/lib/plusCreditOptions';
import { formatCreditBalance } from '@/lib/formatCredits';

/**
 * Renders the dynamic input form for a tool and submits it.
 *
 * The component is intentionally thin: validation is enforced by the backend
 * (see backend/utils/tool_runner.js). We only do client-side "required" checks
 * for snappy UX.
 *
 * Exposes via ref:
 *   - applyExample(example, index): pre-fills the form with a saved example.
 *     Starts from each field's default, then overlays only keys present on
 *     `example` — so switching to a sparser preset does not leave stale values
 *     (e.g. old `lyrics` when the new preset only sets `prompt` + `genre`).
 *   - applyInputs(partial): same reset-then-overlay semantics for declared
 *     fields only (skips unknown keys and null/undefined values). Used for
 *     ?query= overrides and replaying `run.inputs` from history.
 *   - setModel(modelId|null): pins a specific model (or clears it back to
 *     the tool default).
 *   - reset(): clears every field back to its computed default and drops the
 *     active example/model selection.
 * Parents can pass `hideExamples` to suppress the built-in example chip row
 * when they're rendering a richer example presentation themselves.
 */
function ToolForm({
  tool,
  isRunning,
  onSubmit,
  error,
  hideExamples,
  hideSubmitButton = false,
  showCostFooter,
  catalogKind = 'tool',
  /** When true, skip global-catalog model fallback until curated picker data arrives. */
  deferModelFallback = false,
}, ref) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isMD } = useResponsiveness();
  const { openModal } = useModalStore(state => state.actions);
  const user = useAuthStore(state => state.user);
  const totalAvailableCredits = useAuthStore(state => state.user?.credits?.totalAvailable) ?? 0;
  const isPlus = !!user?.flags?.isPlus;
  const resetDateLabel = formatCreditResetDate(user?.credits?.monthlyAllotment?.resetDate);
  const getModelById = useModelStore(state => state.getModelById);
  const shouldShowCostFooter = showCostFooter ?? !hideSubmitButton;
  const handleBuyCredits = useCallback(() => {
    openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
  }, [openModal]);
  // Plus and short for this run: the choice modal (Arya free / buy / Premium
  // / refresh date) rather than a straight jump into the credit store.
  const handleSeeCreditOptions = useCallback(() => {
    openModal('OutOfCreditsModal', { source: 'tool_form' });
  }, [openModal]);
  const fields = useMemo(() => {
    const list = Array.isArray(tool?.fields) ? [...tool.fields] : [];
    return list
      .filter(f => f?.flags?.isVisibleToUsers !== false)
      .sort((a, b) => (a?.ui?.order ?? 0) - (b?.ui?.order ?? 0));
  }, [tool]);

  const fieldNames = useMemo(() => new Set(fields.map(f => f?.name).filter(Boolean)), [fields]);
  const examples = useMemo(() => {
    const list = Array.isArray(tool?.examples) ? tool.examples : [];
    return list
      .filter(ex => ex && typeof ex === 'object' && !Array.isArray(ex))
      .map(ex => Object.fromEntries(
        Object.entries(ex).filter(([key, value]) => (
          fieldNames.has(key)
          && value !== undefined
          && value !== null
          && value !== ''
          && (!Array.isArray(value) || value.length > 0)
        ))
      ))
      .filter(ex => Object.keys(ex).length > 0);
  }, [tool, fieldNames]);

  // Compute defaults from each field's validation/value. We DON'T memoize
  // this on `fields` (which can rebuild on parent re-renders) because that
  // would clobber user input every time the tool detail re-fetches.
  const computeDefaults = useCallback((flds) => {
    const out = {};
    for (const f of flds) {
      const def = f?.validation?.default;
      const fallback = f?.value;
      out[f.name] = def !== undefined ? def : fallback;
    }
    return out;
  }, []);

  const [values, setValues] = useState(() => computeDefaults(fields));
  const [touched, setTouched] = useState({});
  const [localErrors, setLocalErrors] = useState({});
  // Single-model tools track one id (`modelId`); multi-model tools
  // (`tool.flags.supportsMultipleModels`) track an ordered list (`modelIds`).
  // We keep them as separate pieces of state — instead of always-array — so
  // the existing single-model code paths (cost calc, run button, default
  // pill) don't need any branching, and so reverting `multi → single` on a
  // tool just works without stale array state.
  const [modelId, setModelId] = useState(null);
  const [modelIds, setModelIds] = useState([]);
  const [activeExampleIndex, setActiveExampleIndex] = useState(null);

  const supportsMultipleModels = !!tool?.flags?.supportsMultipleModels;
  const maxModels = Math.max(1, Number(tool?.modelFilter?.maxModels || 1));

  // Only reset form state when the tool itself changes (by slug), NOT
  // every time the tool detail object re-loads with new field references.
  const toolKey = tool?.slug || tool?.id;
  const lastInitKeyRef = React.useRef(null);
  React.useEffect(() => {
    if (!toolKey) return;
    if (lastInitKeyRef.current === toolKey) return;
    lastInitKeyRef.current = toolKey;
    setValues(computeDefaults(fields));
    setTouched({});
    setLocalErrors({});
    setModelId(null);
    setModelIds([]);
    setActiveExampleIndex(null);
  }, [toolKey, fields, computeDefaults]);

  // Pre-select the tool/prompt default model when the detail payload arrives.
  // Music generator prefers Music 2.6 (instrumental-capable) even if the DB
  // still points defaultModel at Music 2.0 until tools are reseeded.
  useEffect(() => {
    if (!toolKey) return;
    const selectionTool = {
      slug: tool?.slug,
      defaultModel: tool?.defaultModel,
      suggestedModels: tool?.suggestedModels,
    };
    const preferred = getPreferredToolModel(selectionTool);
    const id = preferred?.id ? String(preferred.id) : null;
    if (!id) return;
    setModelId(prev => prev || id);
    setModelIds(prev => (prev.length ? prev : [id]));
    const seed = [tool?.defaultModel, ...(tool?.suggestedModels || [])].filter(Boolean);
    if (seed.length) useModelStore.getState().addModels(seed);
  }, [toolKey, tool?.defaultModel, tool?.suggestedModels, tool?.slug]);

  // Prompts (and tools without curated models): resolve a modality-matching model
  // from the global catalog so the picker is never empty.
  useEffect(() => {
    if (!toolKey || tool?.defaultModel?.id || deferModelFallback) return;

    let cancelled = false;
    (async () => {
      const ms = useModelStore.getState();
      await Promise.all([
        ms.fetchDefaultOutputModels?.().catch(() => {}),
        ms.fetchAllModels?.(1, { autoLoadAll: true }).catch(() => {}),
      ]);
      if (cancelled) return;

      const fallback = getFallbackToolPickerModels(tool, ms);
      if (fallback.length) ms.addModels(fallback);

      let pick = fallback[0] || null;
      if (catalogKind === 'prompt' && tool?.intentAction) {
        pick = (await ms.resolveDefaultOutputModel?.(tool.intentAction)) || pick;
      }

      if (pick?.id && !cancelled) {
        const id = String(pick.id);
        setModelId((prev) => prev || id);
        setModelIds((prev) => (prev.length ? prev : [id]));
      }
    })();

    return () => { cancelled = true; };
  }, [toolKey, catalogKind, deferModelFallback, tool?.defaultModel?.id, tool?.intentAction, tool?.outputType, tool?.modelFilter, tool?.inputType]);

  const coerceMusicModelId = useCallback((nextValues, requestedId) => {
    return getCompatibleToolModelId(tool, nextValues, requestedId);
  }, [tool]);

  const applyCompatibleModel = useCallback((nextValues, requestedId = modelId) => {
    const compatibleId = coerceMusicModelId(nextValues, requestedId);
    const normalizedCurrent = modelId ? String(modelId) : null;
    if (compatibleId === normalizedCurrent) return;
    setModelId(compatibleId);
    setModelIds(compatibleId ? [compatibleId] : []);
  }, [coerceMusicModelId, modelId]);

  const handleChange = useCallback((name, value) => {
    const nextValues = { ...values, [name]: value };
    setValues(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
    setLocalErrors(prev => ({ ...prev, [name]: null }));
    setActiveExampleIndex(null);
    applyCompatibleModel(nextValues);
  }, [values, applyCompatibleModel]);

  const getExampleLabel = useCallback((example, index) => {
    const firstField = fields.find(f => example[f.name] !== undefined);
    const raw = firstField ? example[firstField.name] : null;
    const display = Array.isArray(raw) ? raw.join(', ') : String(raw || '');
    if (display) return display.length > 42 ? `${display.slice(0, 39)}...` : display;
    return `Example ${index + 1}`;
  }, [fields]);

  const applyExample = useCallback((example, index) => {
    if (!example || typeof example !== 'object') return;
    // Start from schema defaults, then copy only keys the preset actually
    // defines. Shallow-merging onto `prev` would keep e.g. `lyrics` from an
    // earlier preset when the new card omits that field entirely.
    const base = computeDefaults(fields);
    const merged = { ...base };
    const touchedKeys = [];
    for (const f of fields) {
      const name = f?.name;
      if (!name || !Object.prototype.hasOwnProperty.call(example, name)) continue;
      // Placeholder/sample file objects (`_placeholder`) are applied as-is.
      merged[name] = example[name];
      touchedKeys.push(name);
    }
    setValues(merged);
    applyCompatibleModel(merged);
    setTouched(prev => ({
      ...prev,
      ...Object.fromEntries(touchedKeys.map(key => [key, true])),
    }));
    setLocalErrors(prev => ({
      ...prev,
      ...Object.fromEntries(touchedKeys.map(key => [key, null])),
    }));
    setActiveExampleIndex(typeof index === 'number' ? index : null);
  }, [computeDefaults, fields, applyCompatibleModel]);

  const reset = useCallback(() => {
    const defaults = computeDefaults(fields);
    setValues(defaults);
    setTouched({});
    setLocalErrors({});
    setActiveExampleIndex(null);
    applyCompatibleModel(defaults);
  }, [computeDefaults, fields, applyCompatibleModel]);

  // Apply a partial map of field-name → value (skipping anything not declared
  // on the tool). Used by the page to drain URL params, replay history runs,
  // etc. without the caller having to know about our internal state shape.
  const applyInputs = useCallback((partial) => {
    if (!partial || typeof partial !== 'object') return;
    const cleaned = {};
    for (const [key, value] of Object.entries(partial)) {
      if (!fieldNames.has(key)) continue;
      if (value === undefined || value === null) continue;
      cleaned[key] = value;
    }
    if (!Object.keys(cleaned).length) return;
    const base = computeDefaults(fields);
    const merged = { ...base, ...cleaned };
    setValues(merged);
    applyCompatibleModel(merged);
    setTouched(prev => ({
      ...prev,
      ...Object.fromEntries(Object.keys(cleaned).map(k => [k, true])),
    }));
    setLocalErrors(prev => ({
      ...prev,
      ...Object.fromEntries(Object.keys(cleaned).map(k => [k, null])),
    }));
    setActiveExampleIndex(null);
  }, [computeDefaults, fieldNames, fields, applyCompatibleModel]);

  // Imperatively set the model selection. Pass `null` to fall back to the
  // tool's default. Used by the URL-param drain on the page.
  const setModel = useCallback((id) => {
    const compatibleId = coerceMusicModelId(values, id);
    setModelId(compatibleId);
    // Mirror into the multi-model array so multi-aware tools stay in sync
    // when the page only knows the legacy single-model surface (e.g.
    // ?model=foo URL params).
    setModelIds(compatibleId ? [compatibleId] : []);
  }, [coerceMusicModelId, values]);

  // Multi-model setter: drain a full ordered pick set into form state.
  // Caps at `maxModels` defensively even though the picker enforces it.
  const setModelIdsImperative = useCallback((ids) => {
    const arr = Array.isArray(ids) ? ids.filter(Boolean).map(String) : [];
    const capped = arr.slice(0, maxModels);
    setModelIds(capped);
    // Keep modelId in sync with the *primary* (first) pick so cost-calc and
    // any single-model UI bits show something sensible immediately.
    setModelId(capped[0] || null);
  }, [maxModels]);

  // Forward-declare the submit handler so we can expose it on the ref before
  // its definition further down. Parents (notably the sticky native Run CTA)
  // need to fire the same validation + submit pipeline as the in-form button
  // — without copy-pasting the required-field check.
  const handleSubmitRef = useRef(null);

  useImperativeHandle(ref, () => ({
    applyExample,
    applyInputs,
    setModel,
    setModelIds: setModelIdsImperative,
    reset,
    // Lets parents (e.g. a sticky bottom Run bar on phones) trigger the same
    // submit path that the in-form ToolRunButton uses, so client-side
    // required-field validation & cost calc stay in one place.
    submit: () => handleSubmitRef.current?.(),
  }), [applyExample, applyInputs, setModel, setModelIdsImperative, reset]);

  // Resolve the model the run would actually use. Used as a fallback estimate
  // (model.creditCosts.baseCost) before the backend returns an authoritative
  // number, and to show "Free to run" instantly for free-only models.
  const modelFromStore = useModelStore(s => (modelId ? s.getModelById?.(modelId) : null));
  const resolvedRunModel = useMemo(() => {
    if (modelId) {
      if (modelFromStore) return modelFromStore;
      const wanted = String(modelId);
      return [tool?.defaultModel, ...(tool?.suggestedModels || [])]
        .find(m => m?.id && String(m.id) === wanted) || null;
    }
    return getPreferredToolModel(tool);
  }, [modelId, modelFromStore, tool]);

  const fallbackCost = useMemo(() => {
    const estimated = estimateToolRunCreditCost(resolvedRunModel, values, fields);
    if (estimated > 0) return estimated;

    const cc = resolvedRunModel?.creditCosts;
    if (!cc?.isEnabled) return 0;
    const base = Number(cc.baseCost) || 0;
    return base > 0 ? base : 0;
  }, [resolvedRunModel, values, fields]);

  // Reconcile model-bound field values whenever the selected model changes.
  // Voice catalogs (and other model-specific option lists) differ across
  // providers, so a value that was valid for the previous model may not
  // exist on the new one. Drop those silently and replace with the new
  // model's declared default for that parameter — same UX as picking the
  // tool fresh, no broken-looking dropdown.
  const modelKey = resolvedRunModel?.id || resolvedRunModel?._id || null;
  useEffect(() => {
    if (!modelKey) return;
    const params = resolvedRunModel?.parameters || [];
    if (!params.length) return;

    setValues(prev => {
      let changed = false;
      const next = { ...prev };
      for (const f of fields) {
        const bound = f?.bindToModelParameter;
        if (!bound) continue;
        const mp = params.find(p => p?.name === bound);
        if (!mp) continue;
        const opts = mp.validation?.options || [];
        if (!opts.length) continue;
        const allowed = opts.map(o => o?.value);
        const current = prev[f.name];
        if (current === undefined || current === null || !allowed.includes(current)) {
          const fallback = mp.value !== undefined && mp.value !== null && allowed.includes(mp.value)
            ? mp.value
            : allowed[0];
          if (next[f.name] !== fallback) {
            next[f.name] = fallback;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [modelKey, resolvedRunModel, fields]);

  // Authoritative cost from POST /json/tools/:slug/calculate-credits.
  // Same flow chat input uses, so the displayed number matches what the
  // user will actually be charged. We debounce 500ms on input changes,
  // bypass the debounce when the model selection changes (snappy feedback),
  // and keep showing the previous cost while a new request is in flight.
  const [calcCost, setCalcCost] = useState(null);
  const [variableBilling, setVariableBilling] = useState(false);
  const [pricingError, setPricingError] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const calcSeqRef = useRef(0);
  const calcTimerRef = useRef(null);

  // Drop a placeholder file value (sample preset) before sending to the
  // backend — the backend's credit calc would just ignore them, but stripping
  // here keeps the request payload clean and lets us avoid a 400 from
  // strict validators in the future.
  const sanitizeForCalc = useCallback((v) => {
    if (Array.isArray(v)) return v.map(sanitizeForCalc).filter(item => item !== undefined);
    if (v && typeof v === 'object') {
      if (v._placeholder) return undefined;
      if (v.cdnPath && !v.id && !v._id && !v.fileId && !v.imageId && !v.audioId && !v.videoId) return undefined;
    }
    return v;
  }, []);

  const requestCostCalc = useCallback(async (currentValues, currentModelId, currentModelIds) => {
    if (!tool?.slug) return;
    const seq = ++calcSeqRef.current;
    setIsCalculating(true);
    try {
      const cleaned = {};
      for (const [k, v] of Object.entries(currentValues || {})) {
        const next = sanitizeForCalc(v);
        if (next !== undefined) cleaned[k] = next;
      }
      const res = catalogKind === 'prompt'
        ? await promptsApi.calculateCredits(tool.slug, {
          inputs: cleaned,
          modelId: currentModelId || null,
        })
        : await toolsApi.calculateCredits(tool.slug, {
          inputs: cleaned,
          modelId: currentModelId || null,
          // Multi-model tools sum per-model cost on the backend. Single-model
          // tools never populate `modelIds` so the route falls back to its
          // legacy `modelId`-only path.
          modelIds: Array.isArray(currentModelIds) && currentModelIds.length ? currentModelIds : null,
        });
      if (seq !== calcSeqRef.current) return;
      const quote = res?.data?.cost ?? res?.cost;
      const total = quote?.authorizationTotal ?? quote?.total ?? null;
      setVariableBilling(!!quote?.variableBilling);
      setPricingError(null);
      if (typeof total === 'number') setCalcCost(total);
    } catch (error) {
      if (seq !== calcSeqRef.current) return;
      setCalcCost(null);
      setPricingError(error?.message || 'Pricing is unavailable. Please try another option.');
    } finally {
      if (seq === calcSeqRef.current) setIsCalculating(false);
    }
  }, [tool?.slug, catalogKind, sanitizeForCalc]);

  // Snapshot of the multi-pick set for the cost-calc deps. Comparing the
  // joined string keeps the effect from refiring when the array reference
  // changes but the contents don't (every render mints a fresh array via
  // `.slice()`/`.filter()` upstream).
  const modelIdsKey = modelIds.join(',');

  // Debounce input changes; recalc immediately when the model changes.
  useEffect(() => {
    if (calcTimerRef.current) clearTimeout(calcTimerRef.current);
    calcTimerRef.current = setTimeout(() => {
      requestCostCalc(values, modelId, modelIds);
    }, 500);
    return () => { if (calcTimerRef.current) clearTimeout(calcTimerRef.current); };
  }, [values, requestCostCalc]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    requestCostCalc(values, modelId, modelIds);
  }, [modelId, modelIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const creditCost = calcCost != null ? calcCost : fallbackCost;
  const plusCannotCover = isPlus && creditCost > 0 && totalAvailableCredits < creditCost;

  // Detects placeholder file values that came from a preset's sample media.
  // They have a `cdnPath` for preview but no real backend file id, so the
  // backend can't actually run with them — we surface a friendly message.
  const isPlaceholderFileValue = useCallback((v) => {
    if (!v || typeof v !== 'object') return false;
    if (Array.isArray(v)) return v.some(isPlaceholderFileValue);
    if (v._placeholder) return true;
    if (v.cdnPath && !v.id && !v._id && !v.fileId && !v.imageId && !v.audioId && !v.videoId) return true;
    return false;
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isRunning || pricingError || isCalculating) return;

    const errs = {};
    for (const f of fields) {
      const v = values[f.name];
      if (f?.validation?.required) {
        const isEmpty = v == null || v === '' || (Array.isArray(v) && v.length === 0);
        if (isEmpty) {
          errs[f.name] = `${f.ui?.displayName || f.name} is required`;
          continue;
        }
      }
      if (isPlaceholderFileValue(v)) {
        errs[f.name] = `${f.ui?.displayName || f.name} is a sample preview — please upload your own to run.`;
      }
    }
    setLocalErrors(errs);
    if (Object.keys(errs).length) return;

    // Free users with only signup credits: explain buy-credits OR Plus before
    // burning a round-trip that returns the same gate as a red toast.
    const resolveModel = (id) => {
      if (!id) return null;
      const fromStore = getModelById?.(id);
      if (fromStore) return fromStore;
      const wanted = String(id);
      return [tool?.defaultModel, ...(tool?.suggestedModels || [])]
        .find(m => m?.id && String(m.id) === wanted) || null;
    };
    const modelsToGate = supportsMultipleModels && modelIds.length
      ? modelIds.map(resolveModel).filter(Boolean)
      : [resolvedRunModel].filter(Boolean);
    const lockedModel = modelsToGate.find(m => isToolModelPlusLocked(m, user));
    if (lockedModel) {
      openModelAccessPaywall(openModal, { model: lockedModel });
      return;
    }

    onSubmit?.(values, {
      modelId,
      // Only forward `modelIds` for multi-model tools — passing it for
      // single-model tools would just be noise (and the backend does
      // accept both, with `modelIds` winning, so we don't want to
      // accidentally mask the single-model fallback chain in the resolver).
      modelIds: supportsMultipleModels && modelIds.length ? modelIds : null,
      operation: tool?.operation || null,
    });
  }, [
    isRunning,
    pricingError,
    isCalculating,
    fields,
    values,
    onSubmit,
    modelId,
    modelIds,
    supportsMultipleModels,
    tool?.operation,
    tool?.defaultModel,
    tool?.suggestedModels,
    isPlaceholderFileValue,
    getModelById,
    resolvedRunModel,
    user,
    openModal,
  ]);

  // Keep the ref pointer in sync with the latest closure so a sticky external
  // Run button always picks up the freshest `values` / `modelId` etc.
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  const styles = StyleSheet.create({
    container: {
      width: '100%',
    },
    description: {
      marginBottom: 20,
      lineHeight: 22,
    },
    modelFieldWrap: {
      width: '100%',
      marginBottom: 16,
    },
    modelLabelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 8,
      gap: 4,
    },
    submitRow: {
      marginTop: 8,
      gap: 14,
    },
    // Footer block below the run button. Splits into:
    //   left: a balance line + the explanatory copy + buy-credits link
    //   right: the per-run cost number, vertically centered
    // Footer is a vertical stack: balance row (with right-aligned cost),
    // explainer copy, then the secondary "buy more" affordance. Keeping the
    // cost inline with the balance puts the two key numbers (have / need)
    // on the same eyeline like a checkout summary.
    costFooter: {
      flexDirection: 'column',
      gap: 6,
      paddingTop: 12,
      borderTopWidth: 1,
      borderTopColor: colors.border_color_secondary,
    },
    costFooterMobile: {
      paddingTop: 0,
      paddingBottom: 12,
      borderTopWidth: 0,
      borderBottomWidth: 1,
      borderBottomColor: colors.border_color_secondary,
    },
    costBalanceRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    costBalanceLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flex: 1,
      minWidth: 0,
    },
    costBalanceRight: {
      flexDirection: 'row',
      alignItems: 'baseline',
      gap: 4,
      marginLeft: 'auto',
    },
    costBalanceCoin: {
      width: 16,
      height: 16,
      borderRadius: 999,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.solid_color_tertiary,
    },
    costCopy: {
      lineHeight: 17,
    },
    buyCreditsBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingVertical: 5,
      paddingHorizontal: 9,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_tertiary,
      alignSelf: 'flex-start',
      ...(Platform.OS === 'web' && { cursor: 'pointer', transition: 'border-color 0.15s, background-color 0.15s' }),
    },
    buyCreditsBtnHover: {
      borderColor: colors.border_color_primary,
      backgroundColor: colors.solid_color_primary,
    },
    costSuffix: {
      lineHeight: 13,
      opacity: 0.8,
    },
    error: {
      marginTop: 12,
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderRadius: 8,
      backgroundColor: colors.solid_color_tertiary,
      borderWidth: 1,
      borderColor: colors.color_destructive,
    },
    examplesWrap: {
      marginBottom: 16,
      gap: 8,
    },
    examplesRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    exampleChip: {
      paddingHorizontal: 10,
      paddingVertical: 7,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_tertiary,
    },
    exampleChipActive: {
      borderColor: colors.text_color_primary,
      backgroundColor: colors.solid_color_primary,
    },
  });

  const renderCostFooter = (mobile = false) => (
    <View style={[styles.costFooter, mobile && styles.costFooterMobile]}>
      <View style={styles.costBalanceRow}>
        <View style={styles.costBalanceLeft}>
          <View style={styles.costBalanceCoin}>
            <Icon name='coin' size={9} solid color={colors.text_color_secondary} />
          </View>
          <Text size='xs' color='secondary' weight='medium' numberOfLines={1}>
            {(t('pages.tools.balanceLabel') || 'Balance: %COUNT% credits')
              .replace('%COUNT%', formatCreditBalance(totalAvailableCredits))}
          </Text>
        </View>

        <View style={styles.costBalanceRight}>
          {creditCost > 0 ? (
            <>
              <Text size='xs' color='primary' weight='semibold'>
                {formatCreditBalance(creditCost)}
              </Text>
              <Text size='xxs' color='tertiary' style={styles.costSuffix}>
                {(variableBilling ? 'credit hold' : creditCost === 1
                  ? (t('pages.tools.creditPerRunSuffixSingular') || 'credit / run')
                  : (t('pages.tools.creditPerRunSuffix') || 'credits / run'))}
              </Text>
            </>
          ) : (
            <Text size='xs' color='primary' weight='semibold'>
              {t('pages.tools.estimatedCostFree') || 'Free'}
            </Text>
          )}
        </View>
      </View>

      <Text size='xs' color='tertiary' style={styles.costCopy}>
        {pricingError || (variableBilling
          ? 'The displayed credits are a temporary hold for the maximum reply. Your final charge uses actual usage.'
          : creditCost > 0
          ? (t('pages.tools.costExplainer')
            || 'Your request will cost %COUNT% credits per run. Credits are automatically refunded for failed generations (you\u2019re never charged for failures).'
            ).replace('%COUNT%', formatCreditBalance(creditCost))
          : (t('pages.tools.costExplainerFree')
            || 'This request is free to run. You won\u2019t be charged any credits.'))}
      </Text>

      {plusCannotCover && (
        <Text size='xs' color='danger' style={styles.costCopy}>
          {resetDateLabel
            ? t('pages.tools.notEnoughForRun', {
              cost: formatCreditBalance(creditCost),
              count: formatCreditBalance(totalAvailableCredits),
              date: resetDateLabel,
            })
            : t('pages.tools.notEnoughForRunNoDate', {
              cost: formatCreditBalance(creditCost),
              count: formatCreditBalance(totalAvailableCredits),
            })}
        </Text>
      )}

      <Pressable
        onPress={plusCannotCover ? handleSeeCreditOptions : handleBuyCredits}
        style={({ hovered, pressed }) => [
          styles.buyCreditsBtn,
          (hovered || pressed) && styles.buyCreditsBtnHover,
        ]}
        accessibilityRole='button'
        accessibilityLabel={plusCannotCover ? t('pages.tools.seeOptions') : (t('pages.tools.buyMoreCredits') || 'Buy more credits')}
      >
        <Icon name={plusCannotCover ? 'coins' : 'plus'} size={10} color={colors.text_color_primary} />
        <Text size='xs' color='primary' weight='semibold'>
          {plusCannotCover ? t('pages.tools.seeOptions') : (t('pages.tools.buyMoreCredits') || 'Buy more credits')}
        </Text>
      </Pressable>
    </View>
  );

  return (
    <View style={styles.container}>
      {tool?.description && (
        <Text size='n' color='secondary' style={styles.description}>
          {tool.description}
        </Text>
      )}

      {!hideExamples && examples.length > 0 && (
        <View style={styles.examplesWrap}>
          <Text size='xs' color='secondary' weight='medium'>
            {t('pages.tools.examples') || 'Examples'}
          </Text>
          <View style={styles.examplesRow}>
            {examples.map((example, index) => (
              <Pressable
                key={`${toolKey}-example-${index}`}
                style={[
                  styles.exampleChip,
                  activeExampleIndex === index && styles.exampleChipActive,
                ]}
                onPress={() => applyExample(example, index)}
                disabled={isRunning}
              >
                <Text size='xs' color='primary'>{getExampleLabel(example, index)}</Text>
              </Pressable>
            ))}
          </View>
        </View>
      )}

      {/* Background pricing must not make inputs read-only between keystrokes.
          Keep corrections available after pricing errors; gate Run instead. */}
      {fields.map(field => (
        <ToolFormField
          key={field.name}
          field={field}
          value={values[field.name]}
          onChange={(v) => handleChange(field.name, v)}
          disabled={isRunning}
          error={localErrors[field.name]}
          toolSlug={tool?.slug}
          contextValues={values}
          selectedModel={resolvedRunModel}
        />
      ))}

      <View style={styles.modelFieldWrap}>
        <View style={styles.modelLabelRow}>
          <Text size='n' weight='bold' color='primary'>
            {supportsMultipleModels
              ? (t('pages.tools.modelsFieldLabel') || 'Models')
              : (t('pages.tools.modelFieldLabel') || 'Model')}
          </Text>
          {supportsMultipleModels && maxModels > 1 && (
            <Text size='xs' color='secondary'>
              {(t('pages.tools.modelsPickedCount') || '%PICKED% of %MAX%')
                .replace('%PICKED%', modelIds.length)
                .replace('%MAX%', maxModels)}
            </Text>
          )}
        </View>
        <ToolModelButton
          tool={tool}
          multi={supportsMultipleModels}
          maxModels={maxModels}
          value={modelId}
          values={modelIds}
          onChange={supportsMultipleModels ? setModelIdsImperative : setModel}
          disabled={isRunning}
          deferCatalogFallback={deferModelFallback}
        />
      </View>

      {shouldShowCostFooter && hideSubmitButton ? renderCostFooter(false) : null}

      {!hideSubmitButton ? (
        <View style={styles.submitRow}>
          {shouldShowCostFooter && isMD && renderCostFooter(true)}
          <ToolRunButton
            label={tool?.runButtonLabel || t('pages.tools.runButton') || 'Run'}
            loadingLabel={t('pages.tools.runningButton') || 'Running…'}
            icon={tool?.runButtonIcon || 'wand-magic-sparkles'}
            isLoading={isRunning}
            isDisabled={!!pricingError || isCalculating}
            onPress={handleSubmit}
          />
          {shouldShowCostFooter && !isMD && renderCostFooter(false)}
        </View>
      ) : null}

      {error ? (
        <View style={styles.error}>
          <Text color='danger' size='n'>{error}</Text>
        </View>
      ) : null}
    </View>
  );
}

export default React.memo(forwardRef(ToolForm));
